import { useKindeAuth, type KindeContextProps } from '@kinde-oss/kinde-auth-react';

/**
 * v5 `useKindeAuth` throws when `KindeContext` is null. v4's default
 * context was a signed-out object, so the same call rendered. The web
 * sign-in surface has no `KindeProvider` (desktop mounts it in
 * `TauriAuth` only). Calling the SDK hook there crashes the modal,
 * including when Kinde cannot be reached.
 *
 * This hook always calls `useKindeAuth` (stable hook order) and, only
 * for that missing-provider error, returns no-op login/register/logout.
 * Inside `KindeProvider` the live SDK methods are returned unchanged.
 */
const MISSING_PROVIDER_MESSAGE =
  'Oooops! useKindeAuth must be used within a KindeProvider';

export type KindeReactAuthMethods = Pick<
  KindeContextProps,
  'login' | 'register' | 'logout'
>;

const unavailableKindeReactAuth: KindeReactAuthMethods = {
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  logout: () => Promise.resolve(),
};

function isMissingKindeProvider(error: unknown): boolean {
  return error instanceof Error && error.message === MISSING_PROVIDER_MESSAGE;
}

export function useKindeReactAuth(): KindeReactAuthMethods {
  try {
    return useKindeAuth();
  } catch (error) {
    if (isMissingKindeProvider(error)) {
      return unavailableKindeReactAuth;
    }
    throw error;
  }
}

/**
 * The full v5 context inside `KindeProvider`, or null for the missing-provider
 * error only (any other error propagates). For callers that can render above
 * the provider and need the SDK's state, not just its methods.
 */
export function useKindeAuthIfProvided(): KindeContextProps | null {
  try {
    return useKindeAuth();
  } catch (error) {
    if (isMissingKindeProvider(error)) {
      return null;
    }
    throw error;
  }
}
